import React, { useState } from 'react';
import {
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Stack,
  Alert,
  Paper,
  Grid,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'; // Import Firestore functions
import { logEvent } from 'firebase/analytics';
import { db, analytics } from '../api/firebase'; // Import db instance
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom'; // Import Link for Terms of Service

const AddReviewForm = ({ professorId }) => {
  const { user } = useAuth();
  const [ratings, setRatings] = useState({
    teachingQuality: 0,
    treatment: 0,
    gradingAssessment: 0,
    tests: 0,
    attendance: 0
  });
  const [commentValue, setCommentValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false); // State for terms acceptance checkbox
  const { t, isArabic } = useLanguage();

  // Rating criteria configuration with translations
  const ratingCriteria = {
    teachingQuality: { 
      label: t('Teaching Quality', 'جودة التدريس'),
      weight: 20 
    },
    treatment: { 
      label: t('Treatment', 'المعاملة'),
      weight: 20 
    },
    gradingAssessment: { 
      label: t('Grading/Assessment', 'التقييم والدرجات'),
      weight: 20 
    },
    tests: { 
      label: t('Tests', 'الاختبارات'),
      weight: 20 
    },
    attendance: { 
      label: t('Attendance', 'الحضور'),
      weight: 20 
    }
  };

  // Calculate weighted overall rating (1-5 scale)
  const calculateOverallRating = () => {
    const totalWeight = Object.values(ratingCriteria)
      .reduce((sum, criteria) => sum + criteria.weight, 0);
    
    const weightedSum = Object.entries(ratings)
      .reduce((sum, [key, score]) => 
        sum + (score * ratingCriteria[key].weight), 0);
    
    return totalWeight > 0 ? (weightedSum / totalWeight).toFixed(1) : 0;
  };

  const handleRatingChange = (criterion, newValue) => {
    setRatings(prev => ({
      ...prev,
      [criterion]: newValue
    }));
  };

  const handleCommentChange = (event) => {
    setCommentValue(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!professorId) {
      setSubmitError('Lecturer ID is missing. Cannot submit review.');
      return;
    }

    if (!user) {
      setSubmitError(t('You must be signed in to submit a review.', 'يجب أن تكون مسجل الدخول لإرسال تقييم.'));
      return;
    }
    
    if (!acceptTerms) {
      setSubmitError(t('You must accept the Terms of Service to submit a review.', 'يجب عليك قبول شروط الخدمة لإرسال تقييم.'));
      setSubmitSuccess(false);
      return;
    }

    // Validate that ALL rating criteria are provided (must be 1-5, not 0)
    const allCriteriaRated = Object.values(ratings).every(rating => rating > 0);
    if (!allCriteriaRated) {
      const unratedCriteria = Object.entries(ratingCriteria)
        .filter(([key]) => ratings[key] === 0)
        .map(([key, criteria]) => criteria.label);
      
      setSubmitError(
        t(
          `Please rate all criteria. Missing: ${unratedCriteria.join(', ')}`,
          `يرجى تقييم جميع المعايير. مفقود: ${unratedCriteria.join('، ')}`
        )
      );
      setSubmitSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const overallRating = parseFloat(calculateOverallRating());
      
      await addDoc(collection(db, 'reviews'), {
        professorId: professorId,
        userId: user.uid, // Add user ID to track who wrote the review
        userDisplayName: user.displayName || 'Anonymous', // Optional: store display name
        overallRating: overallRating,
        ratings: ratings,
        // Keep legacy rating field for backward compatibility
        rating: overallRating,
        comment: commentValue.trim(),
        reviewText: commentValue.trim(), // Alternative field name for consistency
        createdAt: serverTimestamp(),
      });

      // Track review submission
      logEvent(analytics, 'submit_review', {
        professor_id: professorId,
        overall_rating: overallRating,
        has_comment: commentValue.trim().length > 0,
        comment_length: commentValue.trim().length,
        teaching_quality: ratings.teachingQuality,
        treatment: ratings.treatment,
        grading_assessment: ratings.gradingAssessment,
        tests: ratings.tests,
        attendance: ratings.attendance
      });

      // Success - reset form
      setSubmitSuccess(true);
      setRatings({
        teachingQuality: 0,
        treatment: 0,
        gradingAssessment: 0,
        tests: 0,
        attendance: 0
      });
      setCommentValue('');
      setTimeout(() => setSubmitSuccess(false), 5000);

    } catch (error) {
      console.error("Error adding review: ", error);
      setSubmitError('Failed to submit review. Please try again.');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show sign-in message if user is not authenticated
  if (!user) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('Sign in to write a review', 'سجل دخولك لكتابة تقييم للمحاضر')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('You need to be signed in to rate lecturers and write reviews.', 'تحتاج إلى تسجيل الدخول لتقييم المحاضرين وكتابة المراجعات.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
      <Stack spacing={3}>
        <Typography variant="h6" gutterBottom>
          {t('Rate Lecturer (1-5 stars)', 'قيم المحاضر (1-5 نجوم)')}
        </Typography>
        
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t('Overall Rating', 'التقييم العام')}: {calculateOverallRating()}/5
          </Typography>
          <Typography variant="body2" color="error" sx={{ mb: 2, fontWeight: 500 }}>
            {t('All criteria must be rated (required)', 'جميع المعايير مطلوبة (إجباري)')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={2}>
            {Object.entries(ratingCriteria).map(([key, criteria]) => (
              <Grid item xs={12} sm={6} key={key}>
                <Box sx={{ mb: 2 }}>
                  <Typography component="legend" variant="subtitle1" sx={{ 
                    fontWeight: 600,
                    color: 'text.primary',
                    mb: 1
                  }}>
                    {criteria.label}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    direction: 'ltr', // Force LTR for rating to prevent star flipping
                    justifyContent: isArabic ? 'flex-end' : 'flex-start' // Align properly for RTL/LTR
                  }}>
                    <Rating
                      name={`rating-${key}`}
                      value={ratings[key]}
                      onChange={(event, newValue) => handleRatingChange(key, newValue)}
                      max={5}
                      size="large"
                      disabled={isSubmitting}
                      sx={{
                        direction: 'ltr', // Ensure rating always flows left-to-right
                        '& .MuiRating-icon': {
                          direction: 'ltr' // Ensure individual stars are not flipped
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ 
                      ml: isArabic ? 0 : 1,
                      mr: isArabic ? 1 : 0,
                      color: 'text.secondary'
                    }}>
                      {ratings[key]}/5
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <TextField
          id="review-comment"
          label={t('Your Comment (Optional)', 'تعليقك (اختياري)')}
          multiline
          rows={4}
          value={commentValue}
          onChange={handleCommentChange}
          variant="outlined"
          fullWidth
          disabled={isSubmitting}
        />

        {submitError && !submitSuccess && <Alert severity="error">{submitError}</Alert>}
        {submitSuccess && <Alert severity="success">{t('Review submitted successfully!', 'تم إرسال التقييم بنجاح!')}</Alert>}
        
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptTerms}
              onChange={(event) => setAcceptTerms(event.target.checked)}
              name="acceptTerms"
              color="primary"
              disabled={isSubmitting}
            />
          }
          label={
            <Typography variant="body2">
              {t('I accept the ', 'أوافق على ')}
              <Link
                to="/about"
                style={{
                  color: 'inherit',
                  textDecoration: 'underline'
                }}
              >
                {t('Terms of Service', 'شروط الخدمة')}
              </Link>
              .
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || !Object.values(ratings).every(rating => rating > 0) || !acceptTerms}
          sx={{ alignSelf: 'flex-start' }}
        >
          {isSubmitting ? t('Submitting...', 'جاري الإرسال...') : t('Submit Review', 'إرسال التقييم')}
        </Button>
      </Stack>
    </Box>
  );
};

export default AddReviewForm; 